import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wind, Droplets, AlertTriangle, Activity, Gauge, TreePine } from 'lucide-react';
import { ProcessedAirQualityData, getAQIColorClass, getAQIGradientClass } from '@/services/airQualityService';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';

interface AirQualityDisplayProps {
  data: ProcessedAirQualityData | null;
  loading: boolean;
  error: string | null;
}

const AirQualityDisplay: React.FC<AirQualityDisplayProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wind className="h-5 w-5" />
            Air Quality Index
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-12 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-red-200 bg-red-50/50 dark:border-red-800/30 dark:bg-red-900/10">
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-center space-y-2">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto" />
            <p className="text-sm text-red-600 dark:text-red-400">
              Unable to load air quality data
            </p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="w-full border-amber-200 bg-amber-50/50 dark:border-amber-800/30 dark:bg-amber-900/10">
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-center space-y-2">
            <TreePine className="h-8 w-8 text-amber-500 mx-auto" />
            <p className="text-sm text-amber-600 dark:text-amber-400">
              No air quality data available for this region
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const pollutantIcons: { [key: string]: React.ReactNode } = {
    pm25: <div className="w-2 h-2 rounded-full bg-red-500" />,
    pm10: <div className="w-2 h-2 rounded-full bg-orange-500" />,
    o3: <div className="w-2 h-2 rounded-full bg-blue-500" />,
    no2: <div className="w-2 h-2 rounded-full bg-purple-500" />,
    so2: <div className="w-2 h-2 rounded-full bg-yellow-500" />,
    co: <div className="w-2 h-2 rounded-full bg-gray-500" />
  };

  const pollutantNames: { [key: string]: string } = {
    pm25: 'PM2.5',
    pm10: 'PM10',
    o3: 'Ozone',
    no2: 'NO₂',
    so2: 'SO₂',
    co: 'CO'
  };

  const availablePollutants = Object.entries(data.pollutants)
    .filter(([_, value]) => value !== undefined && value !== null)
    .slice(0, 6);

  return (
    <div className="space-y-4">
      {/* Main AQI Card */}
      <div className={`relative overflow-hidden rounded-xl border border-border bg-gradient-to-br ${getAQIGradientClass(data.aqi)}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent" />
        <div className="relative p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wind className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Air Quality Index</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {data.cityName}
            </Badge>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-5xl font-bold mb-1">{data.aqi}</div>
              <Badge className={`${getAQIColorClass(data.aqi)} border-0 text-sm font-medium`}>
                {data.level}
              </Badge>
              <p className="text-sm text-muted-foreground mt-2">
                {data.description}
              </p>
            </div>

            <div className="text-right">
              <Activity className={`h-12 w-12 ${getAQIColorClass(data.aqi).split(' ')[0]} mb-2`} />
              {data.dominantPollutant && (
                <p className="text-sm text-muted-foreground">
                  Primary: {pollutantNames[data.dominantPollutant] || data.dominantPollutant}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 text-xs text-muted-foreground">
            Last updated: {formatDistanceToNow(new Date(data.lastUpdated))} ago
          </div>
        </div>
      </div>

      {/* Pollutant Breakdown */}
      {availablePollutants.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              Pollutant Levels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {availablePollutants.map(([pollutant, value]) => (
                <div key={pollutant} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card/50">
                  <div className="flex items-center gap-2">
                    {pollutantIcons[pollutant]}
                    <span className="text-sm font-medium">
                      {pollutantNames[pollutant] || pollutant.toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-auto text-lg font-semibold">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Forecast */}
      {data.forecast && data.forecast.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4" />
              3-Day Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.forecast.map((forecast, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card/50">
                  <div>
                    <div className="font-medium text-sm">
                      {new Date(forecast.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {forecast.pollutant}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold">{forecast.aqi}</div>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${getAQIColorClass(forecast.aqi)}`}
                    >
                      {forecast.aqi <= 50 ? 'Good' : forecast.aqi <= 100 ? 'Moderate' : 
                       forecast.aqi <= 150 ? 'Unhealthy' : 'Poor'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attribution */}
      <div className="text-xs text-muted-foreground text-center">
        Air quality data provided by{' '}
        <a 
          href="https://waqi.info/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          World Air Quality Index Project
        </a>
      </div>
    </div>
  );
};

export default AirQualityDisplay;